import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { formatPlayerTime } from '../video-detail-panel';

const SKIP_SECONDS = 10;
const MEDIA_EVENTS = ['play', 'pause', 'timeupdate', 'durationchange', 'loadedmetadata', 'volumechange', 'ratechange'];

/*
 * Custom control bar over the inline <video>. Works on the media element itself (play, seek, speed,
 * captions, picture-in-picture) and the stage element (full screen), so it does not depend on
 * Stormcloud internals. Hidden by the player while an ad plays.
 * OnPush + listeners outside Angular: playback updates re-render only this bar.
 */
@Component({
  selector: 'app-vd-player-controls',
  templateUrl: './vd-player-controls.component.html',
  styleUrls: ['./vd-player-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VdPlayerControlsComponent implements OnChanges, OnDestroy {
  @Input() video: HTMLVideoElement | null = null;
  @Input() stage: HTMLElement | null = null;
  @Input() theatre = false;
  @Input() title = '';
  @Output() theatreToggle = new EventEmitter<void>();

  readonly speeds = [0.5, 1, 1.25, 1.5, 2];
  readonly canPip = typeof document !== 'undefined' && !!(document as any).pictureInPictureEnabled;
  paused = true;
  current = 0;
  duration = 0;
  muted = false;
  volume = 1;
  rate = 1;
  hasCaptions = false;
  captionsOn = false;
  speedOpen = false;
  private unbind: (() => void)[] = [];

  constructor(private cdr: ChangeDetectorRef, private zone: NgZone) {}

  get progress(): string { return this.duration ? (this.current / this.duration * 100).toFixed(2) + '%' : '0%'; }
  get timeLabel(): string { return `${formatPlayerTime(this.current)} / ${formatPlayerTime(this.duration)}`; }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.video) this.bind();
  }

  togglePlay(): void {
    const v = this.video;
    if (!v) return;
    if (v.paused) v.play().catch(() => undefined); else v.pause();
  }

  skip(seconds: number): void {
    if (this.video) this.video.currentTime = Math.min(Math.max(0, this.video.currentTime + seconds), this.duration || Infinity);
  }

  seek(value: string): void {
    if (this.video && this.duration) this.video.currentTime = Number(value) / 1000 * this.duration;
  }

  toggleMute(): void { if (this.video) this.video.muted = !this.video.muted; }

  setVolume(value: string): void {
    if (!this.video) return;
    this.video.volume = Number(value) / 100;
    this.video.muted = this.video.volume === 0;
  }

  setRate(rate: number): void {
    if (this.video) this.video.playbackRate = rate;
    this.speedOpen = false;
  }

  toggleCaptions(): void {
    const track = this.video?.textTracks?.[0];
    if (!track) return;
    this.captionsOn = track.mode !== 'showing';
    track.mode = this.captionsOn ? 'showing' : 'hidden';
  }

  togglePip(): void {
    const doc: any = document;
    if (doc.pictureInPictureElement) doc.exitPictureInPicture().catch(() => undefined);
    else (this.video as any)?.requestPictureInPicture?.().catch(() => undefined);
  }

  toggleFullscreen(): void {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
    else this.stage?.requestFullscreen?.().catch(() => undefined);
  }

  /** Keyboard on the focused player: Space / K play-pause, ← → 10 s, M mute, F full screen. */
  onKey(event: KeyboardEvent): void {
    const actions: Record<string, () => void> = {
      ' ': () => this.togglePlay(), k: () => this.togglePlay(),
      ArrowLeft: () => this.skip(-SKIP_SECONDS), ArrowRight: () => this.skip(SKIP_SECONDS),
      m: () => this.toggleMute(), f: () => this.toggleFullscreen(),
    };
    const action = actions[event.key];
    if (action) { event.preventDefault(); action(); }
  }

  ngOnDestroy(): void {
    this.unbind.forEach(off => off());
    this.unbind = [];
  }

  private bind(): void {
    this.ngOnDestroy();
    const v = this.video;
    if (!v) return;
    const sync = () => this.sync();
    this.zone.runOutsideAngular(() => MEDIA_EVENTS.forEach(name => {
      v.addEventListener(name, sync);
      this.unbind.push(() => v.removeEventListener(name, sync));
    }));
    this.sync(false);
  }

  /** Reads the media state; re-renders this bar only (the first read happens inside Angular's own check). */
  private sync(render = true): void {
    const v = this.video;
    if (!v) return;
    this.paused = v.paused;
    this.current = v.currentTime || 0;
    this.duration = isFinite(v.duration) ? v.duration : 0;
    this.muted = v.muted;
    this.volume = v.volume;
    this.rate = v.playbackRate;
    this.hasCaptions = (v.textTracks?.length || 0) > 0;
    if (render) this.cdr.detectChanges();
  }
}
